'use client';

import { RadioGroup as RadioGroupPrimitive, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ComponentPropsWithoutRef, Fragment } from 'react';

type RootProps = ComponentPropsWithoutRef<typeof RadioGroupPrimitive>;

export type RadioGroupOption<T extends string = string> = {
  value: T;
  label: string;
  description?: string;
  tooltip?: React.ReactNode;
};

type RadioGroupProps<T extends string = string> = {
  options: RadioGroupOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  className?: string;
  itemClassName?: string;
  indicatorClassName?: string;
  labelClassName?: string;
  withDividers?: boolean;
  dividerClassName?: string;
} & Omit<RootProps, 'value' | 'onValueChange'>;

export function RadioGroup<T extends string = string>({
  options,
  value,
  onValueChange,
  className,
  itemClassName,
  indicatorClassName,
  labelClassName,
  withDividers = false,
  dividerClassName = 'h-px bg-primary-stroke-day mx-0',
  ...rootProps
}: RadioGroupProps<T>) {
  return (
    <RadioGroupPrimitive
      value={value}
      onValueChange={(val) => onValueChange(val as T)}
      className={cn('flex flex-col gap-0 rounded-[12px] bg-white', className)}
      {...rootProps}
    >
      {options.map((option, index) => {
        const tooltip = option.tooltip;
        return (
          <Fragment key={option.value}>
            <Label
              className={cn(
                'group items-start gap-3 py-3 outline-none px-[16px] font-normal cursor-pointer',
                itemClassName,
                {
                  relative: !!tooltip,
                },
              )}
            >
              <RadioGroupItem value={option.value} className={cn('mt-0.5', indicatorClassName)} />

              <span className='flex flex-col gap-[2px] min-w-0 flex-1 text-start'>
                <span className={cn('text-[14px]', labelClassName)}>{option.label}</span>
                {option.description && <span className='text-[12px] text-text-disabled-day'>{option.description}</span>}
              </span>

              {tooltip}
            </Label>

            {withDividers && index < options.length - 1 ? <div className={cn('w-full', dividerClassName)} /> : null}
          </Fragment>
        );
      })}
    </RadioGroupPrimitive>
  );
}
